import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock,
  FileText,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useExamStore, formatTime, getTimeWarning } from '@/stores/examStore';
import { useMonitoringStore } from '@/stores/monitoringStore';
import { useWebSocketStore } from '@/stores/websocketStore';
import { useUIStore } from '@/stores/uiStore';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Progress } from '@/components/ui/Progress';
import { Modal } from '@/components/ui/Modal';
import { StatusIndicator } from '@/components/domain';
import { BrandMark } from '@/components/shared/BrandMark';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { GazeAccuracyChip } from '@/components/monitoring/GazeAccuracyChip';
import { WebGazerCalibration } from '@/components/monitoring/WebGazerCalibration';
import { cn } from '@/utils/cn';
import { useMotionSafe } from '@/utils/motion';
import { activeProctorLabels } from '@/utils/proctorFlags';
import { apiClient } from '@/api/client';
import {
  monitoringResultFromGaze,
  resetClientProctorState,
} from '@/monitoring/clientProctorFlags';
import {
  MIN_CALIBRATION_ACCURACY,
  canStartExam,
} from '@/monitoring/webgazerCalibrationState';
import {
  clearWebGazerData,
  startWebGazer,
  stopWebGazer,
} from '@/monitoring/webgazerMonitor';
import {
  disposeFaceLandmarkerHeadPose,
  isFaceLandmarkerRunning,
  startFaceLandmarkerHeadPose,
  stopFaceLandmarkerHeadPose,
} from '@/monitoring/faceLandmarkerHeadPose';
import {
  cameraActiveSuccessMessage,
  shouldSurfaceMonitoringError,
} from '@/monitoring/studentReadiness';

interface ThesisDemo {
  isActive: boolean;
  startTime: number | null;
  stats: {
    fixed: { alerts: number; totalFrames: number };
    adaptive: { alerts: number; totalFrames: number };
    agreements: number;
    disagreements: number;
  };
}

const ExamInterface: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fadeRise } = useMotionSafe();
  const demoModeEnabled = searchParams.get('demo') === '1';
  const { user, sessionId } = useAuthStore();
  const { addNotification } = useUIStore();
  
  // Exam store state
  const {
    questions,
    answers,
    currentQuestion,
    timeRemaining,
    isLoading,
    isSubmitting,
    error,
    autoSaveEnabled,
    loadQuestions,
    setAnswer,
    submitExam,
    nextQuestion,
    previousQuestion,
    getProgress,
    enableAutoSave,
    resetExam,
  } = useExamStore();

  // Monitoring store state
  const {
    gazeData,
    currentData,
    isCalibrating,
    calibrationInfo,
    connect: connectMonitoring,
    disconnect: disconnectMonitoring,
    startMonitoring,
    addMonitoringData,
    updateCalibration,
    processedFrame,
    systemUsed,
    error: monitoringError,
  } = useMonitoringStore();

  // Derived values from monitoring data
  const alertLevel = currentData?.alertLevel || 'NORMAL';
  // Calibrated only when overlay completed AND accuracy ≥ 75% (or store canStartExam)
  const isCalibrated =
    !isCalibrating &&
    (calibrationInfo?.canStartExam === true ||
      (calibrationInfo?.status === 'completed' &&
        canStartExam(calibrationInfo.accuracy ?? 0)));
  const confidence = currentData?.confidenceScore || 0;
  const proctorObservations = activeProctorLabels(currentData?.behavioralFlags);
  const calibrationAccuracy = calibrationInfo?.accuracy;
  /** Prefer live currentData — never coerce missing pose to 0°. */
  const liveYaw =
    currentData?.headPose?.yaw ?? currentData?.gazeData?.yaw ?? gazeData?.yaw ?? null;
  const livePitch =
    currentData?.headPose?.pitch ??
    currentData?.gazeData?.pitch ??
    gazeData?.pitch ??
    null;
  const formatPoseDeg = (v: number | null | undefined): string =>
    v == null || Number.isNaN(v) ? '—' : `${v.toFixed(0)}°`;

  // WebSocket store state
  const { isConnected, connect, disconnect } = useWebSocketStore();

  // Local state
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [showGazeHelpModal, setShowGazeHelpModal] = useState(false);
  const [showTechDetail, setShowTechDetail] = useState(false);
  const [landmarkerArmed, setLandmarkerArmed] = useState(false);
  const [thesisDemo, setThesisDemo] = useState<ThesisDemo>({
    isActive: false,
    startTime: null,
    stats: {
      fixed: { alerts: 0, totalFrames: 0 },
      adaptive: { alerts: 0, totalFrames: 0 },
      agreements: 0,
      disagreements: 0,
    },
  });

  // Video ref for camera stream
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const frameIntervalRef = useRef<number | null>(null);
  const lastCriticalAlertRef = useRef<string | null>(null);

  const attentionStrip = useMemo(() => {
    if (alertLevel === 'CRITICAL_ALERT') {
      return {
        tone: 'critical' as const,
        message: 'Return your focus to the exam questions immediately.',
      };
    }
    if (alertLevel === 'WARNING' || alertLevel === 'DISTRACTED') {
      return {
        tone: 'warning' as const,
        message: 'Please keep your attention on the exam.',
      };
    }
    return null;
  }, [alertLevel]);

  const integrityStatus =
    !isConnected
      ? ('offline' as const)
      : alertLevel === 'CRITICAL_ALERT'
        ? ('critical' as const)
        : alertLevel === 'WARNING' || alertLevel === 'DISTRACTED'
          ? ('warning' as const)
          : ('nominal' as const);

  // Initialize camera stream
  const initializeCamera = useCallback(async () => {
    console.log('🎥 [ExamInterface] Initializing camera...');
    
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported');
      }

      console.log('📹 [ExamInterface] Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });

      console.log('✅ [ExamInterface] Camera stream obtained:', stream);

      // Wait for video element to be available with retries
      let retryCount = 0;
      const maxRetries = 5;
      
      while (!videoRef.current && retryCount < maxRetries) {
        console.log(`🔄 [ExamInterface] Waiting for video element (${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 50));
        retryCount++;
      }

      if (videoRef.current) {
        console.log('🔗 [ExamInterface] Setting video srcObject...');
        videoRef.current.srcObject = stream;
        
        // Set camera active immediately when stream is obtained
        setCameraActive(true);
        
        console.log('▶️ [ExamInterface] Starting video playback...');
        try {
          await videoRef.current.play();
          console.log('✅ [ExamInterface] Video playing successfully');
          
          addNotification({
            type: 'success',
            title: 'Camera Active',
            message: cameraActiveSuccessMessage(),
          });
        } catch (playError) {
          console.error('❌ [ExamInterface] Video play failed:', playError);
          // Keep camera active even if autoplay fails
        }
      } else {
        console.error('❌ [ExamInterface] Video ref is null after retries');
        throw new Error('Video element not available after retries');
      }
    } catch (error) {
      console.error('❌ [ExamInterface] Camera initialization failed:', error);
      setCameraActive(false);
      addNotification({
        type: 'error',
        title: 'Camera Error',
        message: `Failed to access camera: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    }
  }, [addNotification]);

  // Cleanup camera stream
  const cleanupCamera = useCallback(() => {
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
    
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  }, []);

  // Start on-device WebGazer + independent Face Landmarker head pose
  const startClientGazeMonitoring = useCallback(async () => {
    if (!sessionId) return;
    resetClientProctorState();
    let lastPost = 0;
    let lastGaze: { x: number; y: number } | null = null;

    const publish = (
      gaze: { x: number; y: number } | null,
      opts?: { skipGazeHistory?: boolean }
    ) => {
      const result = monitoringResultFromGaze(gaze, sessionId, {
        skipGazeHistory: opts?.skipGazeHistory,
      });
      addMonitoringData(result);

      const now = Date.now();
      if (now - lastPost < 2000) return;
      lastPost = now;
      const flags = activeProctorLabels(result.behavioralFlags);
      apiClient
        .postMonitoringEvent({
          session_id: sessionId,
          alert_level: result.alertLevel,
          focus_percentage: result.focusPercentage,
          flags,
          behavioral_flags: result.behavioralFlags as unknown as Record<string, boolean>,
          gaze_x: gaze?.x ?? null,
          gaze_y: gaze?.y ?? null,
          timestamp: new Date().toISOString(),
        })
        .catch(() => {
          /* non-blocking */
        });
    };

    try {
      const video = videoRef.current;
      if (video) {
        try {
          await startFaceLandmarkerHeadPose(video, () => {
            setLandmarkerArmed(true);
            // Head-pose-only ticks must not inflate the WebGazer missing-face streak.
            publish(lastGaze, { skipGazeHistory: lastGaze == null });
          });
          setLandmarkerArmed(isFaceLandmarkerRunning());
        } catch (poseErr) {
          console.error('Face Landmarker start failed:', poseErr);
          setLandmarkerArmed(false);
          addNotification({
            type: 'warning',
            title: 'Head pose limited',
            message: 'Face Landmarker could not start. Gaze monitoring continues.',
          });
        }
      } else {
        console.warn('[QuizSecure] No #cameraVideo yet — head pose deferred');
      }

      await startWebGazer((gaze) => {
        lastGaze = gaze;
        publish(gaze);
      });
    } catch (err) {
      console.error('WebGazer start failed:', err);
      addNotification({
        type: 'error',
        title: 'Gaze unavailable',
        message: 'On-device WebGazer could not start. Allow camera and reload.',
      });
    }
  }, [sessionId, addMonitoringData, addNotification]);

  // Stop legacy frame capture helpers (ETH path unused)
  const stopFrameCapture = useCallback(() => {
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
  }, []);

  // Initialize exam interface
  useEffect(() => {
    // Check authentication state once on mount
    const { isAuthenticated: currentAuth, sessionId: currentSessionId, user: currentUser } = useAuthStore.getState();
    
    if (!currentAuth || !currentSessionId || currentUser?.role !== 'student') {
      navigate('/login', { replace: true });
      return;
    }

    const initializeExam = async () => {
      try {
        // Connect WebSocket and wire monitoring store listeners
        await connect(currentSessionId);
        connectMonitoring(currentSessionId);
        startMonitoring();
        
        // Load exam questions
        await loadQuestions(currentSessionId);
        
        // Wait for video element to be available, then initialize camera
        setTimeout(async () => {
          try {
            await initializeCamera();
          } catch (cameraError) {
            console.warn('Camera initialization failed, continuing without camera:', cameraError);
          }
        }, 100);
        
        // Check calibration status
        if (!isCalibrated) {
          setShowCalibrationModal(true);
        } else {
          // Enable auto-save
          enableAutoSave(currentSessionId);
        }
      } catch (error) {
        console.error('Failed to initialize exam:', error);
        addNotification({
          type: 'error',
          title: 'Initialization Error',
          message: 'Failed to initialize exam. Please refresh the page.',
        });
      }
    };

    initializeExam();

    // Cleanup on unmount
    return () => {
      disconnect();
      disconnectMonitoring();
      resetExam();
      cleanupCamera();
      stopFaceLandmarkerHeadPose();
      void disposeFaceLandmarkerHeadPose();
      void stopWebGazer();
    };
  }, [navigate, initializeCamera, cleanupCamera]); // Added camera functions

  // Handle calibration completion
  useEffect(() => {
    if (isCalibrated && showCalibrationModal) {
      setShowCalibrationModal(false);
      enableAutoSave(sessionId!);
      void startClientGazeMonitoring();
      addNotification({
        type: 'success',
        title: 'Calibration Complete',
        message: 'On-device WebGazer is ready. You may begin the exam.',
      });
    }
  }, [
    isCalibrated,
    showCalibrationModal,
    sessionId,
    enableAutoSave,
    addNotification,
    startClientGazeMonitoring,
  ]);

  // Backup: if already calibrated / modal skipped, still arm trackers once camera is live.
  const monitoringStartedRef = useRef(false);
  useEffect(() => {
    if (!isCalibrated || !cameraActive || !sessionId || monitoringStartedRef.current) return;
    monitoringStartedRef.current = true;
    void startClientGazeMonitoring();
  }, [isCalibrated, cameraActive, sessionId, startClientGazeMonitoring]);

  // If student-page cleanup raced and dropped the socket, reconnect while calibrating
  useEffect(() => {
    if (!showCalibrationModal || isConnected || !sessionId) return;
    const timer = window.setTimeout(() => {
      connect(sessionId).catch((err) => {
        console.warn('Calibration websocket reconnect failed:', err);
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [showCalibrationModal, isConnected, sessionId, connect]);

  // Critical alerts interrupt; warnings stay non-blocking on the attention strip
  useEffect(() => {
    if (alertLevel === 'CRITICAL_ALERT') {
      const message = 'Please return your focus to the exam questions immediately.';
      setAlertMessage(message);
      if (lastCriticalAlertRef.current !== alertLevel) {
        lastCriticalAlertRef.current = alertLevel;
        setShowAlertModal(true);
      }
      return;
    }
    lastCriticalAlertRef.current = null;
    if (alertLevel === 'NORMAL' || alertLevel === 'DISTRACTED') {
      setShowAlertModal(false);
    }
  }, [alertLevel]);

  // Camera preview only — gaze is on-device (no frame upload)
  useEffect(() => {
    stopFrameCapture();
  }, [stopFrameCapture]);

  // Handle answer selection — blocked until ≥75% calibration gate passes
  const handleAnswerSelect = useCallback(
    (questionIndex: number, answerIndex: number) => {
      if (!isCalibrated || showCalibrationModal) {
        setShowCalibrationModal(true);
        return;
      }
      setAnswer(questionIndex, answerIndex);
    },
    [isCalibrated, showCalibrationModal, setAnswer]
  );

  const handleRecalibrate = useCallback(() => {
    clearWebGazerData();
    updateCalibration({
      status: 'not_started',
      canStartExam: false,
      accuracy: undefined,
      progress: 0,
      message: 'Recalibration required before continuing the exam.',
    });
    setShowGazeHelpModal(false);
    setShowCalibrationModal(true);
  }, [updateCalibration]);

  // Handle exam submission
  const handleSubmitExam = useCallback(async () => {
    if (!sessionId) return;

    try {
      setShowSubmitModal(false);
      const result = await submitExam(sessionId);
      
      addNotification({
        type: 'success',
        title: 'Exam Submitted',
        message: `Your exam has been submitted successfully. Score: ${result.score.toFixed(1)}%`,
      });
      
      navigate('/exam-complete');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Submission Failed',
        message: 'Failed to submit exam. Please try again.',
      });
    }
  }, [sessionId, submitExam, addNotification, navigate]);

  // Handle thesis demo toggle
  const toggleThesisDemo = useCallback(async () => {
    if (!sessionId) return;

    if (thesisDemo.isActive) {
      // Deactivate demo
      try {
        const response = await fetch('/api/deactivate-demo-mode', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `session_id=${encodeURIComponent(sessionId)}`,
        });
        
        if (response.ok) {
          setThesisDemo(prev => ({ ...prev, isActive: false, startTime: null }));
          addNotification({
            type: 'info',
            title: 'Demo Deactivated',
            message: 'Thesis demonstration mode has been deactivated.',
          });
        }
      } catch (error) {
        console.error('Failed to deactivate demo:', error);
      }
    } else {
      // Activate demo
      try {
        const response = await fetch('/api/activate-demo-mode', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `session_id=${encodeURIComponent(sessionId)}`,
        });
        
        if (response.ok) {
          setThesisDemo(prev => ({
            ...prev,
            isActive: true,
            startTime: Date.now(),
            stats: {
              fixed: { alerts: 0, totalFrames: 0 },
              adaptive: { alerts: 0, totalFrames: 0 },
              agreements: 0,
              disagreements: 0,
            },
          }));
          addNotification({
            type: 'info',
            title: 'Demo Activated',
            message: 'Thesis demonstration mode is now active.',
          });
        }
      } catch (error) {
        console.error('Failed to activate demo:', error);
      }
    }
  }, [sessionId, thesisDemo.isActive, addNotification]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    if (sessionId) {
      try {
        await fetch('/api/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `session_id=${encodeURIComponent(sessionId)}`,
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    useAuthStore.getState().logout();
    navigate('/');
  }, [sessionId, navigate]);

  const progress = getProgress();
  const timeWarning = getTimeWarning(timeRemaining);
  const currentQuestionData = questions[currentQuestion];

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas font-sans text-content-primary">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-hairline border-t-ink" />
          <p>Loading exam...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas p-4 font-sans text-content-primary">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full border border-state-critical/25 bg-state-critical-soft">
              <AlertCircle className="size-8 text-state-critical" aria-hidden="true" />
            </div>
            <h2 className="mb-4 font-display text-heading-sm font-normal">Error Loading Exam</h2>
            <p className="mb-6 text-content-secondary">{typeof error === 'string' ? error : 'An unexpected error occurred'}</p>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas font-sans text-content-primary">
      {/* Header */}
      <nav className="border-b border-subtle bg-surface-canvas">
        <div className="mx-auto flex max-w-page flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <BrandMark />
            <div>
              <h1 className="font-display text-heading-sm font-normal">QuizSecure</h1>
              <p className="text-sm font-medium text-content-muted">Exam in Progress</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
            {/* Timer */}
            <div className="flex min-h-11 items-center gap-3 rounded-control border border-hairline bg-surface-card px-3 py-2">
              <div className={cn(
                "size-2.5 rounded-full",
                timeRemaining <= 0
                  ? "bg-state-critical"
                  : timeRemaining <= 600
                    ? "bg-state-warning"
                    : "bg-state-neutral"
              )} />
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-content-muted sm:text-sm">Time:</span>
                <span className={cn(
                  "text-base font-580 tabular-nums sm:text-lg",
                  timeRemaining <= 0
                    ? "text-state-critical"
                    : timeRemaining <= 600
                      ? "text-state-warning"
                      : "text-content-primary"
                )}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </div>

            {/* Progress */}
            <div className="flex min-h-11 items-center gap-3 rounded-control border border-hairline bg-surface-card px-3 py-2">
              <CheckCircle2 className="size-5 text-content-secondary" aria-hidden="true" />
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-content-muted sm:text-sm">Progress:</span>
                <span className="text-base font-580 tabular-nums text-content-primary sm:text-lg">
                  {progress.answered} of {progress.total}
                </span>
              </div>
            </div>

            {isCalibrated &&
              !showCalibrationModal &&
              calibrationAccuracy != null && (
                <GazeAccuracyChip
                  accuracy={calibrationAccuracy}
                  onRecalibrate={handleRecalibrate}
                  onHelp={() => setShowGazeHelpModal(true)}
                />
              )}

            {/* Student Name */}
            <div className="col-span-2 px-1 text-sm text-content-secondary sm:col-span-1">
              <span className="font-medium">{user?.name || 'Student'}</span>
            </div>

            {/* Thesis Demo — gated behind ?demo=1 */}
            {demoModeEnabled && (
              <Button
                size="sm"
                variant={thesisDemo.isActive ? "primary" : "secondary"}
                onClick={toggleThesisDemo}
                className="text-sm"
              >
                {thesisDemo.isActive ? 'Demo Active' : 'Start Demo'}
              </Button>
            )}

            {/* Exit Button */}
            <ThemeToggle />
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={handleLogout}
            >
              <LogOut className="size-4" aria-hidden="true" />
              Exit
            </Button>
          </div>
        </div>
      </nav>

      {/* Non-blocking attention strip (WARNING + CRITICAL) */}
      {attentionStrip && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            "flex items-center justify-center border-b px-4 py-3 text-center text-sm font-medium sm:px-6",
            attentionStrip.tone === 'critical'
              ? "border-state-critical/25 bg-state-critical-soft text-state-critical"
              : "border-state-warning/25 bg-state-warning-soft text-state-warning"
          )}
        >
          <span>{attentionStrip.message}</span>
        </div>
      )}

      {/* Time Warning */}
      {timeWarning && (
        <div className={cn(
          "flex items-center justify-center border-b px-4 py-3 text-center font-medium sm:px-6",
          timeRemaining <= 0
            ? "border-state-critical/25 bg-state-critical-soft text-state-critical"
            : "border-state-warning/25 bg-state-warning-soft text-state-warning"
        )}>
          <Clock className="mr-3 size-5 shrink-0" aria-hidden="true" />
          <span className="font-medium">{timeWarning}</span>
        </div>
      )}

      {/* Main Content */}
      <main className="mx-auto min-h-screen max-w-page px-4 py-6 sm:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
          {/* Exam Section (60% - 3 columns) */}
          <section className="lg:col-span-3">
            <motion.div {...fadeRise}>
            <Card className="flex h-full flex-col">
              <CardHeader>
                <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-heading-sm font-normal">
                      Question {currentQuestion + 1} of {questions.length}
                    </h2>
                    <p className="mt-1 text-sm font-medium text-content-muted">Select the best answer</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusIndicator
                      status={integrityStatus}
                      label={
                        integrityStatus === 'offline'
                          ? 'Offline'
                          : integrityStatus === 'critical'
                            ? 'Critical'
                            : integrityStatus === 'warning'
                              ? 'Warning'
                              : 'Nominal'
                      }
                      pulse={integrityStatus !== 'nominal' && integrityStatus !== 'offline'}
                    />
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-content-secondary">Progress</span>
                    <span className="font-580 tabular-nums text-content-primary">{progress.percentage.toFixed(0)}% Complete</span>
                  </div>
                  <Progress value={progress.percentage} className="h-2" />
                </div>
              </CardHeader>

              <CardContent className="flex flex-1 flex-col p-5 sm:p-6">
                {/* Question Content */}
                {currentQuestionData && (
                  <>
                    {/* Question Text */}
                    <div className="mb-8 flex-1 rounded-card border border-hairline bg-surface-nested p-5 sm:p-6">
                      <div className="text-lg font-medium leading-relaxed text-content-primary">
                        {currentQuestionData.question}
                      </div>
                    </div>

                    {/* Answer Options */}
                    <div className="mb-8 space-y-3">
                      {currentQuestionData.options?.map((option: string, index: number) => {
                        const isSelected = answers[currentQuestion] === index;
                        return (
                          <div
                            key={index}
                            className={cn(
                              "group cursor-pointer rounded-card border p-4 transition-[background-color,border-color,box-shadow] duration-200 focus-within:ring-2 focus-within:ring-ink focus-within:ring-offset-2 focus-within:ring-offset-parchment sm:p-5",
                              isSelected 
                                ? "border-ink bg-surface-nested" 
                                : "border-hairline bg-surface-card hover:border-ink"
                            )}
                            onClick={() => handleAnswerSelect(currentQuestion, index)}
                          >
                            <label className="flex cursor-pointer items-start gap-3 sm:gap-4">
                              <div className="relative mt-1 shrink-0">
                                <input
                                  type="radio"
                                  name={`question-${currentQuestion}`}
                                  value={index}
                                  checked={isSelected}
                                  onChange={() => handleAnswerSelect(currentQuestion, index)}
                                  className="sr-only"
                                />
                                <div className={cn(
                                  "flex size-5 items-center justify-center rounded-full border-2 transition-colors duration-200",
                                  isSelected 
                                    ? "border-ink bg-ink" 
                                    : "border-hairline group-hover:border-ink"
                                )}>
                                  {isSelected && (
                                    <div className="size-2 rounded-full bg-parchment" />
                                  )}
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start gap-3">
                                  <span className={cn(
                                    "flex size-8 shrink-0 items-center justify-center rounded-control font-ui text-sm font-580",
                                    isSelected 
                                      ? "bg-ink text-parchment" 
                                      : "bg-surface-nested text-content-secondary"
                                  )}>
                                    {String.fromCharCode(65 + index)}
                                  </span>
                                  <span className={cn(
                                    "pt-1 font-ui text-base leading-relaxed",
                                    isSelected ? "font-medium text-content-primary" : "text-content-secondary"
                                  )}>
                                    {option}
                                  </span>
                                </div>
                              </div>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Navigation Controls */}
                <div className="flex items-center justify-between gap-3 border-t border-hairline pt-4">
                  <Button
                    variant="secondary"
                    onClick={previousQuestion}
                    disabled={currentQuestion === 0}
                    className="px-4 sm:px-6"
                  >
                    <ChevronLeft className="size-4" aria-hidden="true" />
                    Previous
                  </Button>

                  <div className="flex gap-3">
                    {currentQuestion === questions.length - 1 ? (
                      <Button
                        variant="primary"
                        onClick={() => setShowSubmitModal(true)}
                        loading={isSubmitting}
                        className="px-5 sm:px-8"
                      >
                        <CheckCircle2 className="size-5" aria-hidden="true" />
                        Submit Exam
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={nextQuestion}
                        disabled={currentQuestion >= questions.length - 1}
                        className="px-5 sm:px-6"
                      >
                        Next
                        <ChevronRight className="size-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            </motion.div>
          </section>

          {/* Monitoring aside — peripheral to the question */}
          <aside className="space-y-5 lg:col-span-2">
            {/* Compact supervised camera */}
            <section className="overflow-hidden rounded-card border border-hairline bg-surface-card">
              <div className="flex items-center justify-between gap-3 border-b border-subtle px-4 py-3">
                <div>
                  <p className="text-caption font-medium uppercase tracking-[0.14em] text-content-muted">
                    Supervised
                  </p>
                  <p className="mt-0.5 text-sm text-content-secondary">Camera presence</p>
                </div>
                <StatusIndicator
                  status={cameraActive && isConnected ? 'nominal' : cameraActive ? 'warning' : 'offline'}
                  label={cameraActive && isConnected ? 'Active' : cameraActive ? 'Connecting' : 'Offline'}
                  pulse={false}
                />
              </div>
              <div className="relative aspect-[16/10] max-h-48 bg-obsidian sm:max-h-56">
                <video
                  ref={videoRef}
                  id="cameraVideo"
                  className={cn(
                    'h-full w-full object-cover',
                    processedFrame ? 'absolute inset-0 opacity-0 pointer-events-none' : ''
                  )}
                  autoPlay
                  muted
                  playsInline
                />

                {processedFrame && (
                  <img
                    src={`data:image/jpeg;base64,${processedFrame}`}
                    alt="Camera monitoring preview"
                    className="h-full w-full object-cover"
                  />
                )}

                {/* Live head-pose readout — "—" means landmarker has not produced a pose */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-obsidian/70 px-3 py-1.5 text-caption tabular-nums text-paper-white">
                  Head yaw {formatPoseDeg(liveYaw)} · pitch {formatPoseDeg(livePitch)}
                  {currentData?.behavioralFlags?.headMovementExcessive
                    ? ' · TURNED'
                    : ''}
                </div>
              </div>
              <div className="border-t border-subtle px-4 py-2">
                {shouldSurfaceMonitoringError(monitoringError) && (
                  <p className="mb-2 text-sm text-state-critical">{monitoringError}</p>
                )}
                {proctorObservations.length > 0 ? (
                  <ul className="mb-2 space-y-1">
                    {proctorObservations.map((label) => (
                      <li
                        key={label}
                        className="text-sm font-medium text-state-warning"
                      >
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mb-2 text-sm text-content-muted">No integrity flags</p>
                )}
                <button
                  type="button"
                  onClick={() => setShowTechDetail((open) => !open)}
                  className="text-xs font-medium text-content-muted underline-offset-2 hover:text-content-primary hover:underline"
                  aria-expanded={showTechDetail}
                >
                  {showTechDetail ? 'Hide tech detail' : 'Show tech detail'}
                </button>
                {showTechDetail && (
                  <div className="mt-2 space-y-1 text-caption tabular-nums text-content-secondary">
                    <p>
                      Head yaw={formatPoseDeg(liveYaw)} pitch={formatPoseDeg(livePitch)}
                    </p>
                    <p>
                      Gaze x=
                      {gazeData?.x != null && !Number.isNaN(gazeData.x)
                        ? gazeData.x.toFixed(0)
                        : '—'}{' '}
                      y=
                      {gazeData?.y != null && !Number.isNaN(gazeData.y)
                        ? gazeData.y.toFixed(0)
                        : '—'}
                    </p>
                    {systemUsed && <p className="break-words">{systemUsed}</p>}
                    <p>
                      Confidence:{' '}
                      {confidence ? `${(confidence * 100).toFixed(1)}%` : '—'}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {isCalibrated && (
              <p
                className={cn(
                  'rounded-control border px-3 py-2 text-sm',
                  landmarkerArmed
                    ? 'border-state-nominal/25 bg-state-nominal-soft text-state-nominal'
                    : 'border-state-warning/25 bg-state-warning-soft text-state-warning'
                )}
              >
                {landmarkerArmed
                  ? 'Gaze calibrated — WebGazer + Face Landmarker head pose active.'
                  : 'Gaze calibrated — WebGazer active (head pose starting or unavailable).'}
              </p>
            )}

            {/* Compact guidelines */}
            <section className="border-y border-hairline py-4">
              <h3 className="text-sm font-medium text-content-secondary">Guidelines</h3>
              <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-content-muted">
                <li>Keep your face visible to the camera</li>
                <li>Focus on the exam questions</li>
                <li>Avoid looking away for extended periods</li>
              </ul>
            </section>

            {/* Compact status row */}
            <section className="flex flex-wrap gap-3 pb-2">
              <StatusIndicator
                status={isConnected ? 'nominal' : 'offline'}
                label={isConnected ? 'Link' : 'Offline'}
                pulse={false}
              />
              <StatusIndicator
                status={cameraActive ? 'nominal' : 'critical'}
                label={cameraActive ? 'Camera' : 'No camera'}
                pulse={false}
              />
              <StatusIndicator
                status={autoSaveEnabled ? 'nominal' : 'offline'}
                label={autoSaveEnabled ? 'Auto-save' : 'No auto-save'}
                pulse={false}
              />
            </section>
          </aside>
        </div>
      </main>

      {/* Submit Confirmation Modal */}
      <Modal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        ariaLabel="Submit exam confirmation"
      >
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-6 flex size-20 items-center justify-center rounded-full border border-hairline bg-surface-nested">
            <FileText className="size-10 text-content-secondary" aria-hidden="true" />
          </div>
          <h3 className="mb-4 font-display text-heading-sm font-normal">Submit Exam?</h3>
          <p className="mb-8 leading-relaxed text-content-secondary">
            Are you sure you want to submit your exam? You have answered{' '}
            <span className="font-580 tabular-nums text-content-primary">{progress.answered}</span> out of{' '}
            <span className="font-580 tabular-nums text-content-primary">{progress.total}</span> questions.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button 
              variant="secondary" 
              onClick={() => setShowSubmitModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmitExam} 
              loading={isSubmitting}
              className="flex-1"
            >
              Submit Exam
            </Button>
          </div>
        </div>
      </Modal>

      {/* Alert Modal */}
      <Modal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        ariaLabel="Monitoring attention alert"
      >
        <div className="mx-auto max-w-md text-center">
          <div className={cn(
            "mx-auto mb-6 flex size-20 items-center justify-center rounded-full border",
            alertLevel === 'CRITICAL_ALERT'
              ? "border-state-critical/25 bg-state-critical-soft text-state-critical"
              : "border-state-warning/25 bg-state-warning-soft text-state-warning"
          )}>
            <CircleAlert className="size-10" aria-hidden="true" />
          </div>
          <h3 className="mb-4 font-display text-heading-sm font-normal">Attention Required</h3>
          <p className="mb-8 leading-relaxed text-content-secondary">{alertMessage}</p>
          <Button 
            onClick={() => setShowAlertModal(false)}
            className="w-full"
          >
            I Understand
          </Button>
        </div>
      </Modal>

      {/* WebGazer look-and-click calibration (on-device) */}
      <WebGazerCalibration
        open={showCalibrationModal}
        onComplete={() => {
          /* monitoringStore updated inside WebGazerCalibration; effect closes overlay */
        }}
      />

      {/* Gaze calibration help (exam chrome — chip menu) */}
      <Modal
        isOpen={showGazeHelpModal}
        onClose={() => setShowGazeHelpModal(false)}
        ariaLabel="Gaze calibration help"
      >
        <div className="mx-auto max-w-lg">
          <h3 className="font-display text-heading-sm font-normal text-content-primary">
            How calibration works
          </h3>
          <ul className="mt-4 list-disc space-y-2 pl-5 text-body text-content-secondary">
            <li>
              Keep your face inside the feedback box — it turns from red to green when tracking is
              good.
            </li>
            <li>A gaze prediction dot shows where WebGazer thinks you are looking.</li>
            <li>
              Click each calibration point 5 times while looking at it (center appears last).
            </li>
            <li>
              After all points, stare at the center dot for 5 seconds without moving the mouse.
            </li>
            <li>Always follow the mouse cursor with your eyes during the whole process.</li>
            <li>
              You need at least {MIN_CALIBRATION_ACCURACY}% measured accuracy to continue to the
              exam.
            </li>
          </ul>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button type="button" onClick={() => setShowGazeHelpModal(false)} className="flex-1">
              Got it
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => {
                setShowGazeHelpModal(false);
                handleRecalibrate();
              }}
            >
              Recalibrate
            </Button>
          </div>
        </div>
      </Modal>

      {/* Hidden canvas retained for optional local preview tooling */}
      <canvas 
        ref={canvasRef} 
        style={{ display: 'none' }} 
        width={640} 
        height={480}
      />
    </div>
  );
};

export default ExamInterface;